<template>
	<div class="d-flex justify-space-between align-center">
		<h1>Usage Dashboard</h1>
		<ToggleSwitch v-model="isExtractMeshBilling" labelOn="Mesh Extraction Billing" labelOff="Mesh Access Billing" />
	</div>
	<v-divider></v-divider>
	<template v-if="hasData">
		<div v-if="apps.length <= 10">
			<v-chip
				v-for="app in apps"
				:key="app"
				class="mr-2 mb-2"
				color="primary"
				:variant="selectedApps.includes(app) ? 'flat' : undefined"
				text-color="white"
				:prepend-icon="selectedApps.includes(app) ? 'fas fa-circle-check' : 'fa-regular fa-circle'"
				@click="toggleAppSelection(app)"
			>
				{{ app }}
			</v-chip>
		</div>
		<v-select
			v-else
			v-model="selectedApps"
			:items="apps"
			label="Select Apps"
			multiple
			chips
			closable-chips
		></v-select>
		<v-divider></v-divider>
		<div class="charts-container" style="min-height: 500px;">
			<div v-if="selectedApps.length > 0">
				<div class="d-flex justify-space-between scorecards-container">
					<ScoreCard :title="'Total Scans'" :metric="totalScans" :subtitle="'this year'" />
					<ScoreCard :title="'Total Scans'" :metric="scansThisQuarter" :subtitle="'this quarter'" />
					<ScoreCard :title="'Total Scans'" :metric="scansThisMonth" :subtitle="'this month'" />
				</div>
				<v-divider></v-divider>
				<LineChart v-if="canRenderChart" :bqData="monthlyUsageData" :selectedApps="selectedApps" />
				<v-divider></v-divider>
				<div class="d-flex justify-space-between usage-table-container">
					<UsageTable :title="'This Year'" :tableData="tableDataThisYear" />
					<UsageTable :title="'This Quarter'" :tableData="tableDataThisQuarter" />
					<UsageTable :title="'This Month'" :tableData="tableDataThisMonth" />
				</div>
			</div>
			<v-card v-else class="pa-10 rounded-large d-flex align-center justify-center fill-height" id="select-app-card">
				<v-card-title class="pa-0">Select an app to display your usage.</v-card-title>
			</v-card>
		</div>
	</template>
	<template v-else>
		<v-card id="no-data-card" class="pa-10 rounded-large">
			<v-card-title class="pa-0">No app data available.</v-card-title>
			<p> If you believe this is in error, please contact <a href="mailto:developers@structure.io">support</a>.</p>
		</v-card>
	</template>

	<LoadingModal v-if="showLoadingModal" :show-modal="showLoadingModal" />
</template>

<script>
import { onMounted, ref, watch, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import LoadingModal from '@/modals/LoadingModal.vue';
import LineChart from '@/components/charts/LineChart.vue';
import ScoreCard from '@/components/charts/ScoreCard.vue';
import UsageTable from '@/components/charts/UsageTable.vue';
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue';

export default {
	name: 'UsageView',
	components: {
		LoadingModal,
		LineChart,
		ScoreCard,
		UsageTable,
		ToggleSwitch,
	},
	setup() {
		const store = useStore();
		const { getAccessTokenSilently, user } = useAuth0();

		const isExtractMeshBilling = ref(false);
		// See https://structureio.atlassian.net/wiki/x/TAAwOg for event names
		const billingMethod = computed(() => isExtractMeshBilling.value ? 'ExtractMesh' : 'AccessToMesh');

		const monthlyUsageData = computed(() => store.getters.getUsageData(billingMethod.value) || []);
		const hasData = computed(() => monthlyUsageData.value.length > 0);
		const showLoadingModal = computed(() => store.state.showLoadingModal);

		const selectedApps = ref([]);
		const canRenderChart = ref(false);

		const getAccessToken = async () => {
			const token = await getAccessTokenSilently();
			return { headers: { Authorization: `Bearer ${token}` } };
		};

		const apps = computed(() => {
			const appsSet = new Set(monthlyUsageData.value.map(item => item.app_name));
			return Array.from(appsSet);
		});

		const filteredData = computed(() => {
			if (selectedApps.value.length === 0) return [];
			return monthlyUsageData.value.filter(item => selectedApps.value.includes(item.app_name));
		});

		const getScansForCurrentQuarter = (data) => {
			const currentYear = new Date().getFullYear();
			const currentQuarter = Math.floor((new Date().getMonth() + 3) / 3);
			const quarterData = data.filter(item => {
				return item.year === currentYear && Math.floor((item.month - 1) / 3) + 1 === currentQuarter;
			});
			return quarterData.reduce((sum, item) => sum + item.total_usage, 0);
		};

		const getScansForCurrentMonth = (data) => {
			const currentYear = new Date().getFullYear();
			const currentMonth = new Date().getMonth() + 1;
			const monthData = data.filter(item => {
				return item.year === currentYear && item.month === currentMonth;
			});
			return monthData.reduce((sum, item) => sum + item.total_usage, 0);
		};

		const getTotalScansForYear = (data) => {
			const currentYear = new Date().getFullYear();
			const yearData = data.filter(item => item.year === currentYear);
			return yearData.reduce((sum, item) => sum + item.total_usage, 0);
		};

		const getTableDataForTimeframe = (data, timeframe) => {
			const tableData = [];
			if (!data) return tableData;

			let startDate, endDate;
			const currentDate = new Date();
			const currentQuarter = Math.floor((currentDate.getMonth() / 3));
			switch (timeframe) {
				case 'year':
					startDate = new Date(currentDate.getFullYear(), 0, 1);
					endDate = new Date(currentDate.getFullYear() + 1, 0, 0);
					break;
				case 'quarter':
					startDate = new Date(currentDate.getFullYear(), currentQuarter * 3, 1);
					endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);
					break;
				case 'month':
					startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
					endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
					break;
				default:
					return tableData;
			}

			const appCounts = {};
			data.forEach(item => {
				const createdDate = new Date(item.year, item.month - 1);
				if (createdDate >= startDate && createdDate <= endDate) {
					if (!appCounts[item.app_name]) {
						appCounts[item.app_name] = item.total_usage;
					} else {
						appCounts[item.app_name] += item.total_usage;
					}
				}
			});

			for (const app in appCounts) {
				tableData.push({ app, scans: appCounts[app] });
			}
			return tableData;
		};

		const totalScans = computed(() => getTotalScansForYear(filteredData.value));
		const scansThisQuarter = computed(() => getScansForCurrentQuarter(filteredData.value));
		const scansThisMonth = computed(() => getScansForCurrentMonth(filteredData.value));
		const tableDataThisYear = computed(() => getTableDataForTimeframe(filteredData.value, 'year'));
		const tableDataThisQuarter = computed(() => getTableDataForTimeframe(filteredData.value, 'quarter'));
		const tableDataThisMonth = computed(() => getTableDataForTimeframe(filteredData.value, 'month'));

		const toggleAppSelection = (app) => {
			const isSelected = selectedApps.value.includes(app);
			if (isSelected) {
				selectedApps.value = selectedApps.value.filter(a => a !== app);
			} else {
				selectedApps.value = [...selectedApps.value, app];
			}
		};

		const dispatchFetchUsage = () => {
			if (user.value && user.value.sub) {
				const userId = user.value.sub;
				store.dispatch('fetchUsageData', { billingMethod: billingMethod.value, userId, getAccessToken });
			}
		};

		watch(isExtractMeshBilling, () => {
			dispatchFetchUsage();
		});

		watch(apps, (newApps) => {
			if (newApps.length > 10) {
				selectedApps.value = [];
			} else {
				selectedApps.value = newApps;
			}
		});

		watch(selectedApps, (newVal) => {
			if (newVal.length > 0) {
				canRenderChart.value = false;
				nextTick(() => {
					canRenderChart.value = true;
				});
			} else {
				canRenderChart.value = false;
			}
		}, { deep: true });

		onMounted(() => {
			dispatchFetchUsage();
		});

		return {
			showLoadingModal,
			monthlyUsageData,
			hasData,
			apps,
			selectedApps,
			totalScans,
			scansThisQuarter,
			scansThisMonth,
			toggleAppSelection,
			tableDataThisMonth,
			tableDataThisQuarter,
			tableDataThisYear,
			isExtractMeshBilling,
			canRenderChart,
		};
	}
}
</script>

<style>
.scorecards-container, .usage-table-container {
	flex-wrap: no-wrap;
}

.charts-container {
	width: 100%;
	margin: 0 auto;
}
</style>